// The status bar along the bottom (#statusbar: #procs, #statmid > #stattrack > the text, #ecount; written by id all
// over the page): the progress bars on the left and the selection on the right stay put, and the text between, when
// it doesn't fit, runs past like a news ticker, a copy following it round. Hovering it (a tap, on touch) opens every
// item in full in a panel just above the bar, over the layout (nothing moves), with the earlier messages above them:
// each new text of a data-log item, and the processes that have ended (logStatus). The wheel over the bar scrolls back
// through them; the panel goes as soon as the mouse leaves the bar.

const SPEED = 60;   // px per second
const GAP = 40;     // px between the line and its copy
const KEEP = 300;   // past messages kept
const OPEN_MS = 120;   // hovering this long opens it: passing over doesn't flash it open

interface Past { t: number; text: string; warn: boolean }
const past: Past[] = [];
let logged: (() => void) | null = null;

/** Into the status history (a message, a process that ended); the same text again just moves to now. */
export function logStatus(text: string, warn = false) {
  text = text.trim();
  if (!text) return;
  const l = past[past.length - 1];
  if (l?.text === text) { l.t = Date.now(); l.warn = warn; } else past.push({ t: Date.now(), text, warn });
  if (past.length > KEEP) past.splice(0, past.length - KEEP);
  logged?.();
}

export function initStatusBar() {
  const bar = document.getElementById('statusbar'), track = document.getElementById('stattrack'), mid = track?.parentElement;
  if (!bar || !track || !mid) return;
  const copy = document.createElement('div');
  copy.className = 'statcopy';
  copy.setAttribute('aria-hidden', 'true');
  copy.hidden = true;
  track.append(copy);
  const full = document.createElement('div');
  full.id = 'statusfull';
  full.hidden = true;
  document.body.append(full);
  const seen = new WeakMap<HTMLElement, string>();

  const items = () => [...track.children].filter(c => c !== copy) as HTMLElement[];
  /** Everything on the bar, left to right: the progress, the text, the selection. */
  const all = () => [...bar.querySelectorAll<HTMLElement>(':scope > span, #stattrack > span')];
  const shown = (c: HTMLElement) => !c.hidden && !!c.textContent?.trim();
  // copies without ids (so getElementById still finds the real ones), classed by them for the CSS
  const dup = (c: HTMLElement) => {
    const k = c.cloneNode(true) as HTMLElement;
    for (const n of [k, ...k.querySelectorAll<HTMLElement>('[id]')]) if (n.id) { n.classList.add(`sid-${n.id}`); n.removeAttribute('id'); }
    return k;
  };
  const still = matchMedia('(prefers-reduced-motion: reduce)');

  let span = 0, x = 0, last = 0, raf = 0;
  const tick = (t: number) => {
    raf = 0;
    if (!span) return;
    if (last) x = (x + (t - last) / 1000 * SPEED) % span;
    last = t;
    track.style.transform = `translateX(${-x}px)`;
    raf = requestAnimationFrame(tick);
  };
  const measure = () => {
    copy.hidden = true;
    mid.classList.remove('scroll');
    const room = mid.clientWidth, w = track.scrollWidth;
    if (w <= room + 1 || still.matches) {
      span = 0; x = 0; last = 0;
      track.style.transform = '';
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      return;
    }
    mid.classList.add('scroll');
    copy.hidden = false;
    copy.replaceChildren(...items().map(dup));
    span = copy.offsetLeft;   // the line and the gap after it: one lap
    x %= span;
    if (!raf) { last = 0; raf = requestAnimationFrame(tick); }
  };
  const row = (cls: string, ...kids: (Node | string)[]) => { const r = document.createElement('div'); r.className = cls; r.append(...kids); return r; };
  const clock = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  // oldest at the top, now at the bottom by the bar: the wheel goes back in time going up
  const drawFull = () => {
    if (full.hidden) return;
    const fromEnd = full.scrollHeight - full.clientHeight - full.scrollTop;
    const now = all().filter(shown).map(c => row('statrow', dup(c)));
    full.replaceChildren(
      ...past.length ? [row('statrow head', 'earlier')] : [],
      ...past.map(p => row(`statrow past${p.warn ? ' warn' : ''}`, Object.assign(document.createElement('time'), { textContent: clock(p.t), dateTime: new Date(p.t).toISOString() }),
        Object.assign(document.createElement('span'), { textContent: p.text }))),
      ...past.length ? [row('statrow head', 'now')] : [],
      ...now.length ? now : [row('statrow help', 'nothing to report')]);
    full.style.bottom = `${bar.getBoundingClientRect().height}px`;
    if (fromEnd < 4) full.scrollTop = full.scrollHeight;   // following now, unless scrolled back
  };
  /** Each data-log item's new text into the history. */
  const note = () => {
    for (const c of bar.querySelectorAll<HTMLElement>('#stattrack > [data-log]')) {
      const t = (c.hidden ? '' : c.title || c.textContent || '').trim();
      if (t === seen.get(c)) continue;
      seen.set(c, t);
      logStatus(t, c.classList.contains('warn'));
    }
  };
  const changed = () => { note(); measure(); drawFull(); };
  logged = drawFull;
  let queued = false;
  new MutationObserver(recs => {
    if (recs.every(r => copy.contains(r.target))) return;
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; changed(); });
  }).observe(bar, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'class'] });
  new ResizeObserver(changed).observe(mid);
  still.addEventListener('change', changed);
  bar.style.setProperty('--statgap', `${GAP}px`);

  let timer = 0;
  const open = (on: boolean) => {
    clearTimeout(timer);
    if (on === !full.hidden) return;
    full.hidden = !on;
    bar.classList.toggle('open', on);
    if (on) { full.scrollTop = 0; drawFull(); full.scrollTop = full.scrollHeight; }
  };
  bar.addEventListener('pointerenter', e => { if (e.pointerType === 'mouse') { clearTimeout(timer); timer = setTimeout(() => open(true), OPEN_MS); } });
  bar.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') open(false); });
  bar.addEventListener('click', e => {
    if ((e.target as HTMLElement).closest('button, a, input, select')) return;   // a process's ✕, etc.
    if ((e as PointerEvent).pointerType === 'mouse') open(true); else open(full.hidden);   // a tap toggles: no hover there
  });
  // the wheel over the bar scrolls the panel
  bar.addEventListener('wheel', e => {
    if (full.hidden) open(true);
    full.scrollTop += e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? full.clientHeight : 1);
    e.preventDefault();
  }, { passive: false });
  addEventListener('pointerdown', e => { if (!full.hidden && !full.contains(e.target as Node) && !bar.contains(e.target as Node)) open(false); }, true);
  addEventListener('keydown', e => { if (!full.hidden && e.key === 'Escape') { open(false); e.stopPropagation(); } }, true);
  changed();
}
